import * as XLSX from 'xlsx';

const AU_API = (window.AUApi && window.AUApi.segregationPlan) || {};
const AU_REGIONS = AU_API.regions || {};
const AU_REGION_NAMES = AU_API.regionNames || {};
const API = Object.freeze({
    REGIONS: Object.freeze(Object.assign({}, AU_REGIONS)),
    REGION_LABELS: Object.freeze(Object.assign({}, AU_REGION_NAMES)),
    SITES: AU_API.sites,
    SITE_DETAIL: AU_API.siteDetail,
    COMMODITY: AU_API.commodity,
    GRADES: AU_API.grades,
    PRICING: AU_API.pricing,
    SITE_GRADE: AU_API.siteGrade,
});

function buildUrl(template, tokens) {
    if (!template) return null;
    return Object.keys(tokens || {}).reduce(
        (url, key) => url.split(`{${key}}`).join(tokens[key]),
        template
    );
}

const REGION_NAMES = Object.keys(API.REGIONS);

const TABLE = Object.freeze({
    SITE_COL_WIDTH: 200,
    HOURS_COL_WIDTH: 260,
    UPDATE_COL_WIDTH: 220,
    GRADE_COL_WIDTH: 130,
    ROWS_VISIBLE: 10,
    TABS_PAGE_SIZE: 5,
    SITE_GRADE_BATCH: 5,
});

const QUERY_PARAMS = Object.freeze({
    REGIONS: 'regions',
    SITES: 'sites',
});

const extractArray = data => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.value)) return data.value;
    return [];
};

const getVal = (obj, ...keys) => {
    for (const k of keys) {
        if (obj[k] != null) return obj[k];
        const pascal = k.charAt(0).toUpperCase() + k.slice(1);
        if (obj[pascal] != null) return obj[pascal];
    }
    return '';
};

const fetchJson = async url => {
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`HTTP ${res.status} — ${url}`);
    return res.json();
};

const formatRefreshTime = (date = new Date()) => {
    try {
        const pad = n => String(n).padStart(2, '0');
        const h24 = date.getHours();
        const h12 = h24 % 12 || 12;
        const ampm = h24 >= 12 ? 'PM' : 'AM';
        const tz =
            Intl && Intl.DateTimeFormat
                ? Intl.DateTimeFormat().resolvedOptions().timeZone
                : 'local';
        return (
            `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(
                date.getDate()
            )} ` +
            `${h12}:${pad(date.getMinutes())}:${pad(
                date.getSeconds()
            )} ${ampm}` +
            ` (${tz})`
        );
    } catch {
        return '';
    }
};

const applyPricingFilters = (allPricing, commodityId, options = {}) => {
    const now = new Date();
    return allPricing.filter(record => {
        if (String(getVal(record, 'commodityId')) !== String(commodityId))
            return false;

        const endDate = getVal(record, 'endDate');
        if (endDate && new Date(endDate) <= now) return false;

        const selectable =
            record.isGrowerReceivalSelectable ??
            record.IsGrowerReceivalSelectable;
        if (selectable === false) return false;

        if (options.extended === false) {
            if (
                (getVal(record, 'paymentTermDescription') || '')
                    .toLowerCase()
                    .includes('extended')
            )
                return false;
        }

        if (options.sustainable === false) {
            if (record.isSustainable || record.IsSustainable) return false;
        }

        return true;
    });
};

const buildPriceLookup = pricing => {
    const lookup = {};
    for (const record of pricing) {
        const site = getVal(record, 'siteDescription', 'siteName') || '';
        const grade = getVal(record, 'grade') || '';
        if (!site || !grade) continue;
        lookup[site] ??= {};
        lookup[site][grade] ??= [];
        lookup[site][grade].push(record);
    }
    return lookup;
};

const bestPrice = records =>
    records.reduce((max, r) => {
        const p = parseFloat(getVal(r, 'price') || 0);
        return p > max ? p : max;
    }, 0);

const findDayIndex = (detail, targetDate) => {
    if (!detail) return -1;
    const dayName = targetDate
        .toLocaleDateString('en-AU', { weekday: 'long' })
        .toLowerCase();
    for (let i = 0; i <= 6; i++) {
        if ((detail[`day${i}`] ?? '').toLowerCase() === dayName) return i;
    }
    return -1;
};

const getDayInfo = (detail, targetDate, closedLabel = '') => {
    const idx = findDayIndex(detail, targetDate);
    if (idx === -1) return { status: closedLabel, time: '' };
    return {
        status: detail[`status${idx}`] || closedLabel,
        time: detail[`time${idx}`] || '',
    };
};

const getTodayMessage = detail => {
    if (!detail) return '';
    const idx = findDayIndex(detail, new Date());
    return idx !== -1 ? detail[`message${idx}`] || '' : '';
};

const humanizeStatus = status => {
    const spaced = String(status ?? '')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .trim();
    return spaced
        ? spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
        : spaced;
};


(function initSegregationPlan() {
    const root = document.querySelector(
        '[data-control="segregationPlanRegions"]'
    );
    if (!root) return;

    const q = sel => root.querySelector(sel);

    const getCommodityRankMap = () => {
        const ranks = new Map();
        for (const row of allPricing) {
            const id = String(getVal(row, 'commodityId') || '');
            const rank = Number(getVal(row, 'commodityRank'));
            if (!id || Number.isNaN(rank)) continue;
            if (!ranks.has(id) || rank < ranks.get(id)) ranks.set(id, rank);
        }
        return ranks;
    };

    const sortCommoditiesByRank = commodities => {
        const pricingRanks = getCommodityRankMap();
        const rankOf = id => {
            const key = String(id);
            if (key in commodityRankMap) return commodityRankMap[key];
            if (pricingRanks.has(key)) return pricingRanks.get(key);
            return Number.MAX_SAFE_INTEGER;
        };
        return [...commodities].sort((a, b) => {
            const diff = rankOf(a.id) - rankOf(b.id);
            if (diff !== 0) return diff;
            return (a.description || '').localeCompare(
                b.description || '',
                undefined,
                { sensitivity: 'base' }
            );
        });
    };

    const cloneTemplate = name => {
        const template = root.querySelector(`[data-template="${name}"]`);
        if (!template) return null;
        const clone = template.cloneNode(true);
        clone.removeAttribute('data-template');
        return clone;
    };

    const findRegion = (el, name) =>
        el?.querySelector(`[data-region="${name}"]`) ?? null;

    const setStatusDot = (el, status) => {
        if (!el) return;
        el.className = `seg-regions__status-dot ${statusDotClass(status)}`;
    };

    const renderHoursRow = (label, info) => {
        const row = cloneTemplate('hoursRow');
        if (!row) return null;
        const labelEl = findRegion(row, 'hoursLabel');
        const dotEl = findRegion(row, 'statusDot');
        const textEl = findRegion(row, 'hoursText');
        if (labelEl) labelEl.textContent = label;
        setStatusDot(dotEl, info.status);
        if (textEl) textEl.textContent = getDisplayText(info);
        return row;
    };

    const renderPriceCell = (cell, state, value = '') => {
        const content = findRegion(cell, 'priceContent');
        const valueEl = findRegion(cell, 'priceValue');
        const dashEl = findRegion(cell, 'priceDash');
        if (!content || !valueEl || !dashEl) return;

        content.hidden = state === 'empty';
        valueEl.hidden = state !== 'value';
        dashEl.hidden = state !== 'dash';
        valueEl.textContent = state === 'value' ? `$${value}` : '';
    };

    const renderTableHeader = (label, modifier) => {
        const source = els.headRow?.querySelector('.seg-regions__th');
        if (!source) return null;
        const th = source.cloneNode(true);
        th.className = 'seg-regions__th';
        th.classList.add(`seg-regions__th--${modifier}`);
        th.textContent = label;
        th.setAttribute('scope', 'col');
        return th;
    };

    const buildSiteDetailHref = siteName => {
        const baseUrl = (root.getAttribute('data-site-detail-base-url') || '')
            .trim()
            .replace(/\/+$/, '');
        return `${baseUrl}/${encodeURIComponent(siteName.toLowerCase())}`;
    };

    const siteNameLinkConfig = Object.freeze({
        className:
            root.getAttribute('data-site-name-link-class') ||
            'seg-regions__site-name seg-regions__site-name--link',
        target: root.getAttribute('data-site-name-link-target') || '_blank',
        rel:
            root.getAttribute('data-site-name-link-rel') ||
            'noopener noreferrer',
    });

    const els = Object.freeze({
        loading: q('[data-region="loading"]'),
        error: q('[data-region="error"]'),
        noResults: q('[data-region="noResults"]'),
        noResultsMsg: q('[data-region="noResultsMsg"]'),
        tableWrap: q('[data-region="tableWrap"]'),
        headRow: q('[data-region="headRow"]'),
        tbody: q('[data-region="tbody"]'),
        refreshTime: q('[data-region="refreshTime"]'),
        refreshBtn: q('[data-region="refreshBtn"]'),
        exportBtn: q('[data-region="exportBtn"]'),
        commodityTabs: q('[data-region="commodityTabs"]'),
        tabsWrap:
            q('[data-region="tabsWrap"]') ??
            q('.seg-regions__commodity-tabs-wrap'),
        refreshRow:
            q('[data-region="refreshRow"]') ?? q('.seg-regions__refresh-row'),
        toolbar: q('[data-region="toolbar"]') ?? q('.seg-regions__toolbar'),
        optionsRow:
            q('[data-region="optionsRow"]') ?? q('.seg-regions__options'),
        tableScroller: q('[data-region="tableScroller"]'),
        table: q('[data-region="table"]'),
        regionDropdown: q('[data-region="regionDropdown"]'),
        regionToggle: q('[data-region="regionToggle"]'),
        regionValue: q('[data-region="regionValue"]'),
        regionMenu: q('[data-region="regionMenu"]'),
        siteDropdown: q('[data-region="siteDropdown"]'),
        siteToggle: q('[data-region="siteToggle"]'),
        siteValue: q('[data-region="siteValue"]'),
        siteMenu: q('[data-region="siteMenu"]'),
        siteSearch: q('[data-region="siteSearch"]'),
        tabsPrev: q('[data-region="tabsPrev"]'),
        tabsNext: q('[data-region="tabsNext"]'),
    });

    const regionDefaultLabel = els.regionValue?.textContent || '';
    const siteDefaultLabel = els.siteValue?.textContent || '';
    const siteAllLabel = els.siteMenu?.dataset.allLabel || '';
    const regionAllLabel = els.regionMenu?.dataset.allLabel || '';
    const formatRegionLabel = regionName =>
        (API.REGION_LABELS[regionName] || regionName).toUpperCase();

    const hoursLabelsEl = q('[data-region="hoursLabels"]');
    const HOURS_LABELS = {
        today: hoursLabelsEl?.dataset.today || '',
        tomorrow: hoursLabelsEl?.dataset.tomorrow || '',
        openingHours: hoursLabelsEl?.dataset.openingHours || '',
        siteUpdate: hoursLabelsEl?.dataset.siteUpdate || '',
    };

    const statusConfigEl = q('[data-region="statusConfig"]');
    const statusDefaultKey = statusConfigEl?.dataset.defaultStatus || '';
    const statusNoTimeValue = (
        statusConfigEl?.dataset.noTime || ''
    ).toLowerCase();
    const normalizeStatusKey = status =>
        String(status ?? '')
            .toLowerCase()
            .replace(/\s/g, '');
    const openStatusSet = new Set(
        (statusConfigEl?.dataset.openStatus || '')
            .split(/\s+/)
            .map(normalizeStatusKey)
            .filter(Boolean)
    );
    const statusDotClass = status =>
        `seg-regions__status-dot--${normalizeStatusKey(status)}`;
    const getDisplayText = ({ status, time }) => {
        const isOpen = openStatusSet.has(normalizeStatusKey(status));
        const hasTime =
            time && (!statusNoTimeValue || time.toLowerCase() !== statusNoTimeValue);
        return isOpen && hasTime ? time : humanizeStatus(status);
    };

    const sharedTooltip = cloneTemplate('siteTooltip');
    if (sharedTooltip) document.body.appendChild(sharedTooltip);

    let allSites = [];
    let allPricing = [];
    let allCommodities = [];
    let rankedCommodities = [];
    let tabsCommodities = [];
    let tabsOffset = 0;
    let activeCommodityId = null;
    let dataReady = false;

    const siteDataMap = {};
    const siteIdMap = {};
    const siteDetailCache = {};
    const siteRegionMap = {};
    const gradesCache = {};
    const siteGradeMap = {};
    const siteCodeMap = {};
    const siteGradeRankMap = {};
    const commodityRankMap = {};

    let excludedSites = new Set();
    let selectedRegions = new Set(REGION_NAMES);

    const getRegionScopedSites = () =>
        allSites.filter(s => Boolean(siteRegionMap[s.description]));

    const getVisibleSites = () =>
        getRegionScopedSites().filter(s => !excludedSites.has(s.description));

    const readUrlState = () => {
        try {
            const params = new URLSearchParams(window.location.search);
            const parseCsv = key =>
                params.has(key)
                    ? params
                          .get(key)
                          .split(',')
                          .map(s => s.trim())
                          .filter(Boolean)
                    : null;
            return {
                regions: parseCsv(QUERY_PARAMS.REGIONS),
                sites: parseCsv(QUERY_PARAMS.SITES),
            };
        } catch {
            return { regions: null, sites: null };
        }
    };

    const writeUrlState = () => {
        try {
            const existing = new URLSearchParams(window.location.search);
            existing.delete(QUERY_PARAMS.REGIONS);
            existing.delete(QUERY_PARAMS.SITES);

            const parts = [];
            for (const [k, v] of existing) {
                parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
            }

            const fullySelected = [...selectedRegions];
            if (fullySelected.length > 0) {
                parts.push(
                    `${QUERY_PARAMS.REGIONS}=${fullySelected.join(',')}`
                );
            }

            const partiallySites = [];
            for (const regionName of REGION_NAMES) {
                if (selectedRegions.has(regionName)) continue;
                const regionSites = allSites.filter(
                    s => siteRegionMap[s.description] === regionName
                );
                for (const s of regionSites) {
                    if (!excludedSites.has(s.description)) {
                        partiallySites.push(s.description);
                    }
                }
            }
            if (partiallySites.length > 0) {
                parts.push(`${QUERY_PARAMS.SITES}=${partiallySites.join(',')}`);
            }

            const qs = parts.join('&');
            const url = `${window.location.pathname}${qs ? '?' + qs : ''}${
                window.location.hash
            }`;
            window.history.replaceState(null, '', url);
        } catch {}
    };

    const applyUrlState = () => {
        const { regions, sites: urlSites } = readUrlState();

        if (regions !== null) {
            const valid = regions.filter(r => REGION_NAMES.includes(r));
            selectedRegions = new Set(valid);
            excludedSites = new Set();
            for (const site of allSites) {
                const region = siteRegionMap[site.description];
                if (region && !selectedRegions.has(region)) {
                    excludedSites.add(site.description);
                }
            }
        }

        if (urlSites !== null) {
            const siteSet = new Set(urlSites);
            for (const site of allSites) {
                const region = siteRegionMap[site.description];
                if (
                    region &&
                    selectedRegions.has(region) &&
                    !siteSet.has(site.description)
                ) {
                    excludedSites.add(site.description);
                }
            }

            for (const regionName of REGION_NAMES) {
                const sites = allSites.filter(
                    s => siteRegionMap[s.description] === regionName
                );
                if (!sites.length) continue;
                const allSelected = sites.every(
                    s => !excludedSites.has(s.description)
                );
                allSelected
                    ? selectedRegions.add(regionName)
                    : selectedRegions.delete(regionName);
            }
        }
    };

    const syncDropdownLabels = () => {
        if (els.regionValue) els.regionValue.textContent = regionDefaultLabel;
        if (els.siteValue) els.siteValue.textContent = siteDefaultLabel;
    };

    const setHidden = (el, hidden) =>
        el?.setAttribute('aria-hidden', hidden ? 'true' : 'false');

    const showChrome = visible => {
        const d = visible ? '' : 'none';
        if (els.tabsWrap) els.tabsWrap.style.display = d;
        if (els.refreshRow) els.refreshRow.style.display = d;
        if (els.toolbar) els.toolbar.style.display = d;
        if (els.optionsRow) els.optionsRow.style.display = d;
    };

    const setExportBtn = enabled => {
        if (!els.exportBtn) return;
        els.exportBtn.disabled = !enabled;
        els.exportBtn.setAttribute('aria-disabled', enabled ? 'false' : 'true');
    };

    const showLoading = () => {
        setHidden(els.loading, false);
        setHidden(els.error, true);
        setHidden(els.noResults, true);
        setHidden(els.tableWrap, true);
        setExportBtn(false);
    };

    const showError = () => {
        setHidden(els.loading, true);
        setHidden(els.error, false);
        setHidden(els.noResults, true);
        setHidden(els.tableWrap, true);
        setExportBtn(false);
    };

    const updateRefreshTime = () => {
        if (els.refreshTime) els.refreshTime.textContent = formatRefreshTime();
    };

    const getFilterState = () => ({
        openingHours: q('[data-filter="openingHours"]')?.checked ?? false,
        cashPricing: q('[data-filter="cashPricing"]')?.checked ?? true,
        extended: q('[data-filter="extended"]')?.checked ?? true,
        sustainable: q('[data-filter="sustainable"]')?.checked ?? true,
    });

    const syncFilterState = () => {
        const cashOn = q('[data-filter="cashPricing"]')?.checked ?? true;
        for (const key of ['extended', 'sustainable']) {
            const cb = q(`[data-filter="${key}"]`);
            const wrap = cb?.closest('.seg-regions__option-info-wrap');
            if (!wrap) continue;
            wrap.style.opacity = cashOn ? '' : '0.4';
            wrap.style.pointerEvents = cashOn ? '' : 'none';
            if (cb) cb.disabled = !cashOn;
        }
    };

    const resolveSiteDetail = async siteName => {
        const fallback = siteDataMap[siteName] || {};
        const siteId =
            siteIdMap[siteName] || getVal(fallback, 'id', 'siteId') || '';
        if (!siteId) return fallback;
        if (siteDetailCache[siteId]) return siteDetailCache[siteId];
        try {
            const raw = await fetchJson(API.SITE_DETAIL + siteId);
            siteDetailCache[siteId] =
                (Array.isArray(raw) ? raw[0] : raw) || fallback;
        } catch {
            siteDetailCache[siteId] = fallback;
        }
        return siteDetailCache[siteId];
    };

    const renderHoursInContainer = (detail, container) => {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        container.replaceChildren();
        const fragment = document.createDocumentFragment();
        const todayRow = renderHoursRow(
            HOURS_LABELS.today,
            getDayInfo(detail, today, statusDefaultKey)
        );
        const tomorrowRow = renderHoursRow(
            HOURS_LABELS.tomorrow,
            getDayInfo(detail, tomorrow, statusDefaultKey)
        );
        if (todayRow) fragment.appendChild(todayRow);
        if (tomorrowRow) fragment.appendChild(tomorrowRow);
        container.appendChild(fragment);
    };

    const renderUpdateInContainer = (detail, container) => {
        const msg = getTodayMessage(detail);
        container.replaceChildren();
        if (!msg) return;
        const updateText = cloneTemplate('updateText');
        if (!updateText) return;
        updateText.textContent = msg;
        container.appendChild(updateText);
    };

    const renderTodayInline = (detail, container) => {
        const today = new Date();
        const idx = findDayIndex(detail, today);
        container.replaceChildren();
        const row = renderHoursRow(HOURS_LABELS.today, {
            status:
                idx !== -1
                    ? detail[`status${idx}`] || statusDefaultKey
                    : statusDefaultKey,
            time: idx !== -1 ? detail[`time${idx}`] || '' : '',
        });
        if (row) container.appendChild(row);
    };

    const fetchAndRenderTodayInline = async (siteName, container) => {
        renderTodayInline(await resolveSiteDetail(siteName), container);
    };

    const fetchAndRenderSiteDetail = async (
        siteName,
        hoursContainer,
        updateContainer
    ) => {
        hoursContainer.replaceChildren();
        const loading = cloneTemplate('hoursLoading');
        if (loading) hoursContainer.appendChild(loading);
        const detail = await resolveSiteDetail(siteName);
        renderHoursInContainer(detail, hoursContainer);
        renderUpdateInContainer(detail, updateContainer);
    };

    const showTooltip = async (siteName, anchorEl) => {
        const detail = await resolveSiteDetail(siteName);
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        const todayInfo = getDayInfo(detail, today, statusDefaultKey);
        const tomorrowInfo = getDayInfo(detail, tomorrow, statusDefaultKey);
        const updateMsg = getTodayMessage(detail);

        if (!sharedTooltip) return;

        const tooltipName = findRegion(sharedTooltip, 'tooltipName');
        const tooltipDot = findRegion(sharedTooltip, 'tooltipStatusDot');
        const tooltipStatus = findRegion(sharedTooltip, 'tooltipStatus');
        const tooltipToday = findRegion(sharedTooltip, 'tooltipToday');
        const tooltipTomorrow = findRegion(sharedTooltip, 'tooltipTomorrow');
        const tooltipUpdate = findRegion(sharedTooltip, 'tooltipUpdate');

        if (tooltipName) tooltipName.textContent = siteName;
        setStatusDot(tooltipDot, todayInfo.status);
        if (tooltipStatus)
            tooltipStatus.textContent = humanizeStatus(todayInfo.status);
        if (tooltipToday) tooltipToday.textContent = getDisplayText(todayInfo);
        if (tooltipTomorrow)
            tooltipTomorrow.textContent = getDisplayText(tomorrowInfo);
        if (tooltipUpdate) {
            tooltipUpdate.textContent = updateMsg;
            tooltipUpdate.hidden = !updateMsg;
        }

        sharedTooltip.style.cssText = 'top:-9999px;left:-9999px';
        sharedTooltip.setAttribute('aria-hidden', 'false');

        const rect = anchorEl.getBoundingClientRect();
        const tipH = sharedTooltip.offsetHeight;
        const tipW = sharedTooltip.offsetWidth;
        const vpW = window.innerWidth || document.documentElement.clientWidth;

        let top = rect.top - tipH - 8;
        let left = rect.left;
        if (top < 8) top = rect.bottom + 8;
        if (left + tipW > vpW - 8) left = vpW - tipW - 8;
        if (left < 8) left = 8;

        sharedTooltip.style.cssText = `top:${top}px;left:${left}px`;
    };

    const hideTooltip = () =>
        sharedTooltip?.setAttribute('aria-hidden', 'true');

    const fetchGradesForCommodity = async commodityId => {
        if (gradesCache[commodityId]) return gradesCache[commodityId];
        try {
            const data = await fetchJson(
                buildUrl(API.GRADES, { commodityId })
            );
            const grades = extractArray(data)
                .map(g => ({
                    name: getVal(g, 'grade', 'gradeName', 'name') || '',
                    rank: parseFloat(getVal(g, 'gradeRank') || 9999),
                }))
                .filter(g => g.name);
            gradesCache[commodityId] = grades;
            return grades;
        } catch {
            gradesCache[commodityId] = [];
            return [];
        }
    };

    const fetchSiteGrades = async sites => {
        const batches = [];
        for (let i = 0; i < sites.length; i += TABLE.SITE_GRADE_BATCH) {
            batches.push(sites.slice(i, i + TABLE.SITE_GRADE_BATCH));
        }
        await Promise.all(
            batches.map(async batch => {
                const filter = batch
                    .map(
                        s =>
                            `(SiteDescription eq '${s.description.replace(
                                /'/g,
                                "''"
                            )}')`
                    )
                    .join(' or ');
                const url = buildUrl(API.SITE_GRADE, { filter });
                try {
                    const data = await fetchJson(url);
                    for (const r of extractArray(data)) {
                        const siteId = r.SiteId || r.siteId || '';
                        const commId = r.CommodityId || r.commodityId || '';
                        const grade = r.PrimaryGrade || r.primaryGrade || '';
                        const gradeRank = parseFloat(
                            r.GradeRank ?? r.gradeRank ?? 9999
                        );
                        const siteDesc =
                            r.SiteDescription || r.siteDescription || '';
                        const siteCode = r.SiteCode || r.siteCode || '';
                        if (!siteId || !commId) continue;
                        const commRank = Number(
                            r.CommodityRank ?? r.commodityRank
                        );
                        if (
                            !Number.isNaN(commRank) &&
                            (!(commId in commodityRankMap) ||
                                commRank < commodityRankMap[commId])
                        ) {
                            commodityRankMap[commId] = commRank;
                        }
                        siteGradeMap[`${siteId}|${commId}`] = true;
                        if (grade) {
                            siteGradeMap[`${siteId}|${commId}|${grade}`] = true;
                            siteGradeRankMap[
                                `${siteId}|${commId}|${grade}`
                            ] = gradeRank;
                        }
                        if (siteDesc && siteCode && !siteCodeMap[siteDesc])
                            siteCodeMap[siteDesc] = siteCode;
                    }
                } catch {}
            })
        );
    };

    const buildGradeColumns = (commodityId, activeSites) => {
        const gradeMap = {};
        const addGrade = (name, rank) => {
            if (!name) return;
            const parsedRank = Number(rank);
            const safeRank = Number.isNaN(parsedRank)
                ? Number.MAX_SAFE_INTEGER
                : parsedRank;
            if (!gradeMap[name] || safeRank < gradeMap[name].rank) {
                gradeMap[name] = { name, rank: safeRank };
            }
        };

        for (const site of activeSites) {
            const siteId = siteIdMap[site.description] || '';
            if (!siteId) continue;
            const prefix = `${siteId}|${commodityId}|`;
            for (const key of Object.keys(siteGradeMap)) {
                if (!key.startsWith(prefix)) continue;
                const gradeName = key.slice(prefix.length);
                addGrade(gradeName, siteGradeRankMap[key]);
            }
        }

        return Object.values(gradeMap).sort((a, b) => {
            if (a.rank !== b.rank) return a.rank - b.rank;
            return a.name.localeCompare(b.name, undefined, {
                sensitivity: 'base',
            });
        });
    };

    const syncRegionCheckboxes = () => {
        if (!els.regionMenu) return;
        els.regionMenu.querySelector('[data-region-all]').checked =
            selectedRegions.size === REGION_NAMES.length;
        for (const cb of els.regionMenu.querySelectorAll(
            '[data-region-name]'
        )) {
            cb.checked = selectedRegions.has(
                cb.getAttribute('data-region-name')
            );
        }
    };

    const syncRegionsFromSites = () => {
        for (const regionName of REGION_NAMES) {
            const sites = allSites.filter(
                s => siteRegionMap[s.description] === regionName
            );
            if (!sites.length) continue;
            const allSelected = sites.every(
                s => !excludedSites.has(s.description)
            );
            allSelected
                ? selectedRegions.add(regionName)
                : selectedRegions.delete(regionName);
        }
        syncRegionCheckboxes();
    };

    const allDropdowns = [els.regionDropdown, els.siteDropdown];

    const closeDropdown = el => {
        if (!el) return;
        el.classList.remove('-open');
        el.querySelector('[aria-expanded]')?.setAttribute(
            'aria-expanded',
            'false'
        );
    };

    const openDropdown = el => {
        allDropdowns.forEach(d => {
            if (d && d !== el) closeDropdown(d);
        });
        if (!el) return;
        el.classList.add('-open');
        el.querySelector('[aria-expanded]')?.setAttribute(
            'aria-expanded',
            'true'
        );
    };

    const closeAllDropdowns = () => allDropdowns.forEach(closeDropdown);

    const buildRegionDropdown = () => {
        if (!els.regionMenu) return;
        els.regionMenu.replaceChildren();

        const makeRegionItem = (
            id,
            label,
            attrName,
            attrValue,
            isAllRow,
            isChecked
        ) => {
            const li = cloneTemplate('dropdownItem');
            if (!li) return null;
            li.classList.toggle('seg-regions__dropdown-item--all', isAllRow);
            li.addEventListener('click', e => e.stopPropagation());

            const lbl = findRegion(li, 'dropdownLabel');
            const input = findRegion(li, 'dropdownInput');
            const text = findRegion(li, 'dropdownText');
            if (!lbl || !input || !text) return null;
            lbl.htmlFor = id;
            input.id = id;
            input.setAttribute(attrName, attrValue);
            input.checked = isChecked;
            text.textContent = label;
            return li;
        };

        const allLi = makeRegionItem(
            'sg-rg-all',
            regionAllLabel,
            'data-region-all',
            '',
            true,
            selectedRegions.size === REGION_NAMES.length
        );
        if (!allLi) return;
        allLi.querySelector('input').addEventListener('change', function() {
            if (this.checked) {
                REGION_NAMES.forEach(r => selectedRegions.add(r));
                excludedSites.clear();
            } else {
                selectedRegions.clear();
                for (const s of allSites) excludedSites.add(s.description);
            }
            syncRegionCheckboxes();
            if (els.regionValue) els.regionValue.textContent = regionDefaultLabel;
            rebuildSiteMenu();
            syncDropdownLabels();
            rebuildTable();
            writeUrlState();
        });
        els.regionMenu.appendChild(allLi);

        REGION_NAMES.forEach((name, idx) => {
            const li = makeRegionItem(
                `sg-rg-${idx}`,
                formatRegionLabel(name),
                'data-region-name',
                name,
                false,
                selectedRegions.has(name)
            );
            if (!li) return;
            li.querySelector('input').addEventListener('change', function() {
                if (this.checked) {
                    selectedRegions.add(name);
                    for (const s of allSites) {
                        if (siteRegionMap[s.description] === name)
                            excludedSites.delete(s.description);
                    }
                } else {
                    selectedRegions.delete(name);
                    for (const s of allSites) {
                        if (siteRegionMap[s.description] === name)
                            excludedSites.add(s.description);
                    }
                }
                els.regionMenu.querySelector('[data-region-all]').checked =
                    selectedRegions.size === REGION_NAMES.length;
                if (els.regionValue) els.regionValue.textContent = regionDefaultLabel;
                rebuildSiteMenu();
                syncDropdownLabels();
                rebuildTable();
                writeUrlState();
            });
            els.regionMenu.appendChild(li);
        });

        syncRegionCheckboxes();
    };

    const rebuildSiteMenu = () => {
        if (!els.siteMenu) return;
        els.siteMenu.replaceChildren();

        const allLi = cloneTemplate('dropdownItem');
        if (!allLi) return;
        allLi.classList.add('seg-regions__dropdown-item--all');
        const allLabel = findRegion(allLi, 'dropdownLabel');
        const allInput = findRegion(allLi, 'dropdownInput');
        const allText = findRegion(allLi, 'dropdownText');
        if (!allLabel || !allInput || !allText) return;
        allLabel.htmlFor = 'sg-site-all';
        allInput.id = 'sg-site-all';
        allInput.setAttribute('data-site-all', '');
        allText.textContent = siteAllLabel;
        allLi.addEventListener('click', e => e.stopPropagation());

        const scopedSites = getRegionScopedSites();

        const allCb = allInput;
        allCb.checked =
            scopedSites.length > 0 &&
            scopedSites.every(s => !excludedSites.has(s.description));

        allCb.addEventListener('change', function() {
            if (this.checked) {
                excludedSites.clear();
                REGION_NAMES.forEach(r => selectedRegions.add(r));
                for (const cb of els.siteMenu.querySelectorAll('[data-site]'))
                    cb.checked = true;
            } else {
                for (const s of scopedSites) excludedSites.add(s.description);
                selectedRegions.clear();
                for (const cb of els.siteMenu.querySelectorAll('[data-site]'))
                    cb.checked = false;
            }
            syncRegionCheckboxes();
            if (els.siteValue) els.siteValue.textContent = siteDefaultLabel;
            rebuildTable();
            syncDropdownLabels();
            writeUrlState();
        });
        els.siteMenu.appendChild(allLi);

        scopedSites.forEach((site, idx) => {
            const checked = !excludedSites.has(site.description);
            const li = cloneTemplate('dropdownItem');
            if (!li) return;
            const label = findRegion(li, 'dropdownLabel');
            const input = findRegion(li, 'dropdownInput');
            const text = findRegion(li, 'dropdownText');
            if (!label || !input || !text) return;
            label.htmlFor = `sg-site-${idx}`;
            input.id = `sg-site-${idx}`;
            input.setAttribute('data-site', site.description);
            input.checked = checked;
            text.textContent = site.description;
            li.addEventListener('click', e => e.stopPropagation());
            input.addEventListener('change', function() {
                this.checked
                    ? excludedSites.delete(site.description)
                    : excludedSites.add(site.description);

                const allSitesCb = els.siteMenu?.querySelector(
                    '[data-site-all]'
                );
                if (allSitesCb) {
                    allSitesCb.checked =
                        scopedSites.length > 0 &&
                        scopedSites.every(
                            s => !excludedSites.has(s.description)
                        );
                }
                syncRegionsFromSites();
                rebuildTable();
                syncDropdownLabels();
                writeUrlState();
            });
            els.siteMenu.appendChild(li);
        });

        if (els.siteSearch) {
            els.siteSearch.value = '';
            els.siteSearch.oninput = e => {
                e.stopPropagation();
                const term = els.siteSearch.value.trim().toLowerCase();
                for (const cb of els.siteMenu.querySelectorAll('[data-site]')) {
                    const name = (
                        cb.getAttribute('data-site') || ''
                    ).toLowerCase();
                    cb.closest('.seg-regions__dropdown-item').style.display =
                        !term || name.includes(term) ? '' : 'none';
                }
            };
            els.siteSearch.addEventListener('click', e => e.stopPropagation());
            els.siteSearch.parentElement?.addEventListener('click', e =>
                e.stopPropagation()
            );
        }
    };

    const renderTabPage = () => {
        if (!els.commodityTabs) return;
        els.commodityTabs.classList.remove('-open');
        els.commodityTabs.replaceChildren();

        if (!els.commodityTabs.dataset.accordionWired) {
            els.commodityTabs.dataset.accordionWired = '1';
            document.addEventListener('click', e => {
                if (!els.commodityTabs.contains(e.target))
                    els.commodityTabs.classList.remove('-open');
            });
        }

        for (const commodity of tabsCommodities) {
            const isActive = String(commodity.id) === String(activeCommodityId);
            const tab = cloneTemplate('commodityTab');
            if (!tab) return;
            tab.classList.toggle('-active', isActive);
            tab.setAttribute('role', 'tab');
            tab.setAttribute('aria-selected', String(isActive));
            tab.textContent = commodity.description;
            tab.dataset.commodityId = commodity.id;
            tab.addEventListener('click', () => {
                if (String(commodity.id) === String(activeCommodityId)) {
                    els.commodityTabs.classList.toggle('-open');
                    return;
                }
                els.commodityTabs.classList.remove('-open');
                activeCommodityId = commodity.id;
                updateTabsOffsetForActiveCommodity();
                renderTabPage();
                rebuildTable();
            });
            els.commodityTabs.appendChild(tab);
        }

        scrollActiveCommodityTabIntoView();
    };

    const scrollActiveCommodityTabIntoView = () => {
        const el = els.commodityTabs;
        const activeTab = el?.querySelector(
            '.seg-regions__commodity-tab.-active'
        );
        if (!el || !activeTab) return;

        window.requestAnimationFrame(() => {
            const wrapRect = el.getBoundingClientRect();
            const tabRect = activeTab.getBoundingClientRect();
            el.scrollLeft += tabRect.left - wrapRect.left;
        });
    };

    const updateTabsOffsetForActiveCommodity = () => {
        if (window.innerWidth <= 860) return;

        const activeIdx = tabsCommodities.findIndex(
            c => String(c.id) === String(activeCommodityId)
        );
        if (activeIdx === -1) return;

        const maxOffset = Math.max(
            0,
            tabsCommodities.length - TABLE.TABS_PAGE_SIZE
        );
        tabsOffset = Math.min(activeIdx, maxOffset);
    };

    const commodityHasData = (commodityId, visibleSites) =>
        visibleSites.some(s => {
            const id = siteIdMap[s.description] || '';
            return id && siteGradeMap[`${id}|${commodityId}`];
        });

    const refreshCommodityTabs = visibleSites => {
        if (!els.commodityTabs) return;
        const sites = visibleSites || getVisibleSites();
        tabsCommodities = rankedCommodities.filter(
            c =>
                String(c.id) === String(activeCommodityId) ||
                commodityHasData(c.id, sites)
        );
        updateTabsOffsetForActiveCommodity();
        renderTabPage();
    };

    const buildCommodityTabs = () => {
        if (!els.commodityTabs) return;
        rankedCommodities = sortCommoditiesByRank(allCommodities);
        activeCommodityId = rankedCommodities[0]?.id ?? null;
        refreshCommodityTabs();
    };

    const applyTableFixedLayout = (gradeCols, hasHoursCol) => {
        if (!els.table) return;
        const extraWidth = hasHoursCol
            ? TABLE.HOURS_COL_WIDTH + TABLE.UPDATE_COL_WIDTH
            : 0;
        const totalWidth =
            TABLE.SITE_COL_WIDTH +
            extraWidth +
            gradeCols.length * TABLE.GRADE_COL_WIDTH;

        els.table.style.tableLayout = 'fixed';
        els.table.style.width = `${totalWidth}px`;
        els.table.style.minWidth = `${totalWidth}px`;

        const headers = [...els.table.querySelectorAll('thead th')];
        let col = 0;
        headers[col++].style.width = `${TABLE.SITE_COL_WIDTH}px`;
        if (hasHoursCol) {
            if (headers[col])
                headers[col++].style.width = `${TABLE.HOURS_COL_WIDTH}px`;
            if (headers[col])
                headers[col++].style.width = `${TABLE.UPDATE_COL_WIDTH}px`;
        }
        for (; col < headers.length; col++)
            headers[col].style.width = `${TABLE.GRADE_COL_WIDTH}px`;
    };

    const setTableMaxHeight = () => {
        if (!els.tableScroller || !els.table) return;
        const thead = els.table.querySelector('thead');
        const firstRow = els.table.querySelector('tbody tr');
        if (!thead || !firstRow || firstRow.offsetHeight === 0) return;
        els.tableScroller.style.maxHeight = `${thead.offsetHeight +
            TABLE.ROWS_VISIBLE * firstRow.offsetHeight}px`;
        els.tableScroller.style.overflowY = 'auto';
    };

    const rebuildTable = () => {
        if (!dataReady) return;
        renderTable();
    };

    const renderTable = () => {
        if (!dataReady) return;
        updateRefreshTime();

        const filters = getFilterState();
        const visibleSites = getVisibleSites();

        refreshCommodityTabs(visibleSites);

        const pricing = applyPricingFilters(allPricing, activeCommodityId, {
            extended: filters.extended,
            sustainable: filters.sustainable,
        });

        const activeSites = visibleSites.filter(s => {
            const siteId = siteIdMap[s.description] || '';
            return siteId && siteGradeMap[`${siteId}|${activeCommodityId}`];
        });

        const gradeCols = buildGradeColumns(activeCommodityId, activeSites);

        const priceLookup = buildPriceLookup(pricing);

        if (!activeSites.length) {
            const noSitesSelected = visibleSites.length === 0;
            const msgEl = els.noResultsMsg || els.noResults?.querySelector('p');
            if (msgEl && els.noResults)
                msgEl.textContent =
                    els.noResults.dataset[
                        noSitesSelected ? 'msgNoSites' : 'msgNoData'
                    ] || '';
            showChrome(!noSitesSelected);
            setHidden(els.tableWrap, true);
            setHidden(els.noResults, false);
            setHidden(els.loading, true);
            setHidden(els.error, true);
            setExportBtn(false);
            syncFilterState();
            return;
        }

        if (els.headRow) {
            while (els.headRow.children.length > 1)
                els.headRow.removeChild(els.headRow.lastChild);
            if (filters.openingHours) {
                const thH = renderTableHeader(HOURS_LABELS.openingHours, 'hours');
                const thU = renderTableHeader(HOURS_LABELS.siteUpdate, 'update');
                if (thH) els.headRow.appendChild(thH);
                if (thU) els.headRow.appendChild(thU);
            }
            for (const g of gradeCols) {
                const th = renderTableHeader(g.name, 'grade');
                if (th) els.headRow.appendChild(th);
            }
        }

        els.tbody.replaceChildren();
        const fragment = document.createDocumentFragment();
        for (const site of activeSites) {
            const siteName = site.description;
            const siteId = siteIdMap[siteName] || '';
            const sitePrices = priceLookup[siteName] || {};

            const tr = cloneTemplate('siteRow');
            if (!tr) continue;
            const removeBtn = findRegion(tr, 'siteRemove');
            const nameLink = findRegion(tr, 'siteNameLink');
            const nameCol = findRegion(tr, 'siteNameCol');
            const inlineEl = findRegion(tr, 'inlineHours');
            if (!removeBtn || !nameLink || !nameCol || !inlineEl) continue;

            removeBtn.setAttribute('aria-label', `Remove ${siteName}`);
            removeBtn.addEventListener('click', () => {
                excludedSites.add(siteName);
                const siteCb = els.siteMenu?.querySelector(
                    `[data-site="${siteName
                        .replace(/\\/g, '\\\\')
                        .replace(/"/g, '\\"')}"]`
                );
                if (siteCb) siteCb.checked = false;
                const allCb = els.siteMenu?.querySelector('[data-site-all]');
                if (allCb) allCb.checked = false;
                hideTooltip();
                syncRegionsFromSites();
                rebuildTable();
                syncDropdownLabels();
                writeUrlState();
            });

            nameLink.className = siteNameLinkConfig.className;
            nameLink.dataset.siteNameLink = 'true';
            nameLink.textContent = siteName;
            nameLink.href = buildSiteDetailHref(siteName);
            nameLink.target = siteNameLinkConfig.target;
            nameLink.rel = siteNameLinkConfig.rel;
            nameLink.addEventListener('mouseenter', () =>
                showTooltip(siteName, nameLink)
            );
            nameLink.addEventListener('mouseleave', hideTooltip);

            const siteHref = nameLink.href;
            tr.addEventListener('click', e => {
                if (e.target.closest('.seg-regions__site-remove')) return;
                if (e.target.closest('[data-site-name-link]')) return;
                window.open(siteHref, '_blank', 'noopener,noreferrer');
            });

            if (!filters.openingHours) {
                fetchAndRenderTodayInline(siteName, inlineEl);
            } else {
                inlineEl.remove();
            }

            if (filters.openingHours) {
                const tdHours = cloneTemplate('hoursCell');
                const hoursDiv = findRegion(tdHours, 'hoursContainer');
                if (!tdHours || !hoursDiv) continue;
                tr.appendChild(tdHours);

                const tdUpdate = cloneTemplate('updateCell');
                if (!tdUpdate) continue;
                tr.appendChild(tdUpdate);

                fetchAndRenderSiteDetail(siteName, hoursDiv, tdUpdate);
            }

            for (const grade of gradeCols) {
                const td = cloneTemplate('priceCell');
                if (!td) continue;

                const records = sitePrices[grade.name];
                const siteHasGrade =
                    siteId &&
                    siteGradeMap[
                        `${siteId}|${activeCommodityId}|${grade.name}`
                    ];

                if (!siteHasGrade) {
                    renderPriceCell(td, 'empty');
                } else if (records?.length && filters.cashPricing) {
                    const best = bestPrice(records);
                    renderPriceCell(
                        td,
                        best > 0 ? 'value' : 'dash',
                        best.toFixed(2)
                    );
                } else {
                    renderPriceCell(
                        td,
                        filters.cashPricing ? 'dash' : 'swatch'
                    );
                }
                tr.appendChild(td);
            }

            fragment.appendChild(tr);
        }
        els.tbody.appendChild(fragment);

        applyTableFixedLayout(gradeCols, filters.openingHours);
        setTimeout(setTableMaxHeight, 0);

        showChrome(true);
        setHidden(els.loading, true);
        setHidden(els.error, true);
        setHidden(els.noResults, true);
        setHidden(els.tableWrap, false);
        setExportBtn(true);
        syncFilterState();
    };

    const exportToExcel = () => {
        const filters = getFilterState();
        const visibleSites = getVisibleSites();

        const rows = [['Site', 'Site code', 'Commodity', 'Grade', 'Price']];

        const siteCodeFromPricing = {};
        for (const r of allPricing) {
            const name = getVal(r, 'siteDescription', 'siteName') || '';
            const code = getVal(r, 'siteCode') || '';
            if (name && code && !siteCodeFromPricing[name]) {
                siteCodeFromPricing[name] = code;
            }
        }

        const sortedCommodities = sortCommoditiesByRank(allCommodities);

        const pricingByCommodity = {};
        for (const commodity of sortedCommodities) {
            const pricing = applyPricingFilters(allPricing, commodity.id, {
                extended: filters.extended,
                sustainable: filters.sustainable,
            });
            pricingByCommodity[commodity.id] = {
                pricing,
                priceLookup: buildPriceLookup(pricing),
            };
        }

        const sortedSites = [...visibleSites].sort((a, b) =>
            a.description.localeCompare(b.description)
        );

        for (const site of sortedSites) {
            const siteName = site.description;
            const siteCode =
                siteCodeFromPricing[siteName] || siteCodeMap[siteName] || '';
            const siteId = siteIdMap[siteName] || '';

            for (const commodity of sortedCommodities) {
                if (!siteId || !siteGradeMap[`${siteId}|${commodity.id}`])
                    continue;

                const { pricing, priceLookup } = pricingByCommodity[
                    commodity.id
                ];
                const sitePrices = priceLookup[siteName] || {};

                const cachedGrades = gradesCache[commodity.id] || [];
                const gradeRankFromApi = {};
                for (const g of cachedGrades)
                    gradeRankFromApi[g.name] = g.rank ?? 9999;

                const gradeSet = new Set();
                if (siteId) {
                    const prefix = `${siteId}|${commodity.id}|`;
                    for (const key of Object.keys(siteGradeMap)) {
                        if (key.startsWith(prefix))
                            gradeSet.add(key.slice(prefix.length));
                    }
                }

                const gradeNames = [...gradeSet].sort((a, b) => {
                    const ra =
                        siteGradeRankMap[`${siteId}|${commodity.id}|${a}`] ??
                        gradeRankFromApi[a] ??
                        parseFloat(
                            (sitePrices[a]?.[0] &&
                                getVal(sitePrices[a][0], 'gradeRank')) ||
                                9999
                        );
                    const rb =
                        siteGradeRankMap[`${siteId}|${commodity.id}|${b}`] ??
                        gradeRankFromApi[b] ??
                        parseFloat(
                            (sitePrices[b]?.[0] &&
                                getVal(sitePrices[b][0], 'gradeRank')) ||
                                9999
                        );
                    return ra - rb;
                });

                for (const gradeName of gradeNames) {
                    const records = sitePrices[gradeName];
                    const price = records?.length ? bestPrice(records) : 0;
                    rows.push([
                        siteName,
                        siteCode,
                        commodity.description,
                        gradeName,
                        price > 0 ? price : '',
                    ]);
                }
            }
        }

        const ws = XLSX.utils.aoa_to_sheet(rows);
        ws['!cols'] = [
            { wch: 24 },
            { wch: 14 },
            { wch: 18 },
            { wch: 12 },
            { wch: 12 },
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Segregation Plan');

        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/octet-stream' });
        const pad = n => String(n).padStart(2, '0');
        const now = new Date();
        const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
            now.getDate()
        )}-${pad(now.getHours())}-${pad(now.getMinutes())}`;
        const filename = `Bunge_Segregation_Plan_${dateStr}.xlsx`;

        const win = window !== window.top ? window.top : window;
        const url = win.URL.createObjectURL(blob);
        const a = win.document.createElement('a');
        a.href = url;
        a.download = filename;
        win.document.body.appendChild(a);
        a.click();
        win.document.body.removeChild(a);
        win.URL.revokeObjectURL(url);
    };

    const wireInfoPopovers = () => {
        for (const btn of root.querySelectorAll('.seg-regions__info-btn')) {
            if (btn.dataset.tooltipWired) continue;
            btn.dataset.tooltipWired = '1';

            const wrap = btn.closest('.seg-regions__option-info-wrap');
            if (!wrap) continue;

            const popover = cloneTemplate('infoPopover');
            if (!popover) continue;
            popover.setAttribute('aria-hidden', 'true');
            popover.setAttribute('role', 'tooltip');
            const text = findRegion(popover, 'infoText');
            if (text)
                text.textContent = btn.getAttribute('data-info-text') || '';
            btn.appendChild(popover);

            btn.addEventListener('click', e => {
                e.stopPropagation();
                const isOpen = popover.getAttribute('aria-hidden') === 'false';
                for (const p of root.querySelectorAll(
                    '.seg-regions__info-popover[aria-hidden="false"]'
                )) {
                    p.setAttribute('aria-hidden', 'true');
                }
                if (!isOpen) {
                    popover.setAttribute('aria-hidden', 'false');
                }
            });

            popover
                .querySelector('.seg-regions__info-close')
                .addEventListener('click', e => {
                    e.stopPropagation();
                    popover.setAttribute('aria-hidden', 'true');
                });
        }
    };

    const init = async () => {
        showLoading();
        dataReady = false;
        for (const key of Object.keys(siteGradeMap)) delete siteGradeMap[key];
        for (const key of Object.keys(siteCodeMap)) delete siteCodeMap[key];
        for (const key of Object.keys(siteGradeRankMap))
            delete siteGradeRankMap[key];
        for (const key of Object.keys(commodityRankMap))
            delete commodityRankMap[key];
        for (const key of Object.keys(siteRegionMap)) delete siteRegionMap[key];

        try {
            const regionSiteLists = REGION_NAMES.map(() => []);
            let regionFetchFailed = false;

            await Promise.all([
                ...REGION_NAMES.map((regionName, regionIdx) =>
                    fetchJson(API.REGIONS[regionName])
                        .then(data => {
                            regionSiteLists[regionIdx] = extractArray(data);
                        })
                        .catch(() => {
                            regionFetchFailed = true;
                        })
                ),

                fetchJson(API.SITES)
                    .then(data => {
                        allSites = extractArray(data)
                            .filter(
                                s =>
                                    s.site ||
                                    s.Site ||
                                    s.description ||
                                    s.Description
                            )
                            .map(s => {
                                const desc =
                                    s.site ||
                                    s.Site ||
                                    s.description ||
                                    s.Description ||
                                    '';
                                siteDataMap[desc] = s;
                                const id =
                                    s.id || s.Id || s.siteId || s.SiteId || '';
                                if (id) siteIdMap[desc] = id;
                                return { description: desc, raw: s };
                            });
                    })
                    .catch(() => {}),

                fetchJson(API.COMMODITY)
                    .then(async data => {
                        const candidates = extractArray(data)
                            .map(c => ({
                                id: getVal(c, 'commodityId', 'id'),
                                description: getVal(
                                    c,
                                    'commodity',
                                    'description',
                                    'commodityDescription',
                                    'name'
                                ),
                            }))
                            .filter(c => c.description);

                        const withGrades = await Promise.all(
                            candidates.map(async c => {
                                const grades = await fetchGradesForCommodity(
                                    c.id
                                ).catch(() => []);
                                return grades?.length ? c : null;
                            })
                        );
                        allCommodities = withGrades.filter(Boolean);
                    })
                    .catch(() => {}),

                fetchJson(API.PRICING)
                    .then(data => {
                        allPricing = extractArray(data);
                    })
                    .catch(() => {}),
            ]);

            if (regionFetchFailed) {
                throw new Error('One or more region endpoints failed to load');
            }

            REGION_NAMES.forEach((regionName, regionIdx) => {
                for (const s of regionSiteLists[regionIdx]) {
                    const name =
                        s.site || s.Site || s.description || s.Description || '';
                    if (name && !(name in siteRegionMap)) {
                        siteRegionMap[name] = regionName;
                    }
                }
            });

            if (!allSites.length && allPricing.length) {
                const seen = new Set();
                for (const r of allPricing) {
                    const name = getVal(r, 'siteDescription', 'siteName') || '';
                    if (name && !seen.has(name)) {
                        seen.add(name);
                        allSites.push({ description: name, raw: {} });
                    }
                }
                allSites.sort((a, b) =>
                    a.description.localeCompare(b.description)
                );
            }

            await fetchSiteGrades(allSites);

            applyUrlState();
            writeUrlState();

            dataReady = true;
            buildRegionDropdown();
            rebuildSiteMenu();
            syncDropdownLabels();
            buildCommodityTabs();
            rebuildTable();
            syncFilterState();
        } catch {
            showError();
        }
    };

    els.regionToggle?.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        els.regionDropdown.classList.contains('-open')
            ? closeDropdown(els.regionDropdown)
            : openDropdown(els.regionDropdown);
    });

    els.siteToggle?.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        els.siteDropdown.classList.contains('-open')
            ? closeDropdown(els.siteDropdown)
            : openDropdown(els.siteDropdown);
    });

    const setupDropdownKeyboard = (toggleEl, dropdownEl, menuEl, searchEl) => {
        if (!toggleEl || !dropdownEl || !menuEl) return;

        const visibleCheckboxes = () =>
            [...menuEl.querySelectorAll('.seg-regions__filter-check')].filter(
                cb =>
                    cb.closest('.seg-regions__dropdown-item')?.style.display !==
                    'none'
            );

        const focusFirst = () =>
            requestAnimationFrame(() => {
                const items = visibleCheckboxes();
                if (items.length) items[0].focus();
            });
        const focusLast = () =>
            requestAnimationFrame(() => {
                const items = visibleCheckboxes();
                if (items.length) items[items.length - 1].focus();
            });
        dropdownEl.addEventListener('focusout', e => {
            if (e.relatedTarget && !dropdownEl.contains(e.relatedTarget)) {
                closeDropdown(dropdownEl);
            }
        });

        toggleEl.addEventListener('keydown', e => {
            const isOpen = dropdownEl.classList.contains('-open');
            if (e.key === 'Escape') {
                if (isOpen) {
                    closeDropdown(dropdownEl);
                    toggleEl.focus();
                    e.preventDefault();
                }
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (!isOpen) openDropdown(dropdownEl);
                focusFirst();
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                if (!isOpen) openDropdown(dropdownEl);
                focusLast();
            }
        });

        menuEl.addEventListener('keydown', e => {
            if (e.key === 'Escape') {
                closeDropdown(dropdownEl);
                toggleEl.focus();
                e.preventDefault();
                return;
            }
            if (e.key === ' ' || e.key === 'Enter') {
                const cb = document.activeElement;
                if (cb?.classList.contains('seg-regions__filter-check')) {
                    e.preventDefault();
                    cb.checked = !cb.checked;
                    cb.dispatchEvent(new Event('change', { bubbles: true }));
                }
                return;
            }
            if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key))
                return;
            e.preventDefault();
            const items = visibleCheckboxes();
            if (!items.length) return;
            const idx = items.indexOf(document.activeElement);
            let next;
            if (e.key === 'ArrowDown')
                next = idx < items.length - 1 ? items[idx + 1] : items[0];
            else if (e.key === 'ArrowUp')
                next = idx > 0 ? items[idx - 1] : items[items.length - 1];
            else if (e.key === 'Home') next = items[0];
            else next = items[items.length - 1];
            next.focus();
        });

        if (searchEl) {
            searchEl.addEventListener('keydown', e => {
                if (e.key === 'Escape') {
                    closeDropdown(dropdownEl);
                    toggleEl.focus();
                    e.preventDefault();
                } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    focusFirst();
                }
            });
        }
    };

    setupDropdownKeyboard(
        els.regionToggle,
        els.regionDropdown,
        els.regionMenu,
        null
    );
    setupDropdownKeyboard(
        els.siteToggle,
        els.siteDropdown,
        els.siteMenu,
        els.siteSearch
    );
    document.addEventListener('click', e => {
        if (!root.contains(e.target)) {
            closeAllDropdowns();
            return;
        }
        if (!allDropdowns.some(d => d?.contains(e.target))) closeAllDropdowns();
    });

    els.refreshBtn?.addEventListener('click', e => {
        e.preventDefault();
        init();
    });
    els.exportBtn?.addEventListener('click', e => {
        e.preventDefault();
        exportToExcel();
    });

    for (const label of root.querySelectorAll('.seg-regions__option-label')) {
        label.addEventListener('click', e => {
            e.preventDefault();
            e.stopPropagation();
            const cb = label.querySelector('input[type="checkbox"]');
            if (!cb) return;
            cb.checked = !cb.checked;
            cb.dispatchEvent(new Event('change'));
            syncFilterState();
        });
    }

    for (const cb of root.querySelectorAll('.seg-regions__option-check')) {
        cb.addEventListener('change', e => {
            e.preventDefault();
            if (dataReady) rebuildTable();
        });
    }

    for (const cb of root.querySelectorAll('.seg-regions__option-check')) {
        cb.addEventListener('keydown', e => {
            if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                cb.checked = !cb.checked;
                cb.dispatchEvent(new Event('change'));
                syncFilterState();
            }
        });
    }
    wireInfoPopovers();

    let lastMobile = window.innerWidth <= 860;
    window.addEventListener('resize', () => {
        const nowMobile = window.innerWidth <= 860;
        if (nowMobile !== lastMobile) {
            lastMobile = nowMobile;
            if (tabsCommodities.length) renderTabPage();
        }
    });

    init();
})();
